import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  InputAdornment,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Pagination,
  List,
  ListItemButton,
  ListItemText,
  Divider,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from '@/hooks/useDebounce';
import { searchService } from '@/services/search/SearchService';
import { useSearchMetadata } from '@/hooks/search';
import type { SearchHit } from '@/types/core/Search';

const PAGE_SIZE = 20;

/**
 * Parse shortcut prefix from query string.
 * E.g., "i:acme" -> { prefix: "i", cleanQuery: "acme" }
 * E.g., "i:" -> { prefix: "i", cleanQuery: "" }
 */
function parseShortcutPrefix(input: string): { prefix: string | null; cleanQuery: string } {
  const match = input.match(/^([a-zA-Z]):(.*)$/);
  if (match) {
    return { prefix: match[1], cleanQuery: match[2].trim() };
  }
  return { prefix: null, cleanQuery: input };
}

export function SearchPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialQuery = searchParams.get('q') || '';
  const initialEntityType = searchParams.get('entityType') || '';
  const initialPage = parseInt(searchParams.get('page') || '1', 10);

  const [query, setQuery] = useState(initialQuery);
  const [entityType, setEntityType] = useState(initialEntityType);
  const [page, setPage] = useState(initialPage);

  const debouncedQuery = useDebounce(query, 300);

  // Fetch metadata for entity type filter
  const { data: metadata } = useSearchMetadata();

  // Parse shortcut prefix from query for display purposes
  const { prefix: shortcutPrefix, cleanQuery } = useMemo(
    () => parseShortcutPrefix(debouncedQuery),
    [debouncedQuery]
  );

  // Find the entity name from shortcut prefix for display
  const shortcutEntityName = useMemo(() => {
    if (!shortcutPrefix || !metadata?.shortcuts) return null;
    return metadata.shortcuts[shortcutPrefix.toLowerCase()] || null;
  }, [shortcutPrefix, metadata]);

  // Determine if query is valid for search
  // Allow: "i:" (browse mode with shortcut prefix) or query with 2+ chars
  const isQueryEnabled = useMemo(() => {
    if (shortcutPrefix) {
      // Shortcut prefix always enables search (browse mode if cleanQuery is empty)
      return true;
    }
    return cleanQuery.length >= 1;
  }, [shortcutPrefix, cleanQuery]);

  // Perform advanced search
  const { data: searchResults, isLoading, isFetching } = useQuery({
    queryKey: ['search', 'advanced', debouncedQuery, entityType, page],
    queryFn: () =>
      searchService.advancedSearch({
        query: debouncedQuery,
        entityType: entityType || undefined,
        skip: (page - 1) * PAGE_SIZE,
        take: PAGE_SIZE,
      }),
    enabled: isQueryEnabled,
  });

  // Update URL when search params change
  useEffect(() => {
    const params = new URLSearchParams();
    if (debouncedQuery) params.set('q', debouncedQuery);
    if (entityType) params.set('entityType', entityType);
    if (page > 1) params.set('page', page.toString());
    setSearchParams(params, { replace: true });
  }, [debouncedQuery, entityType, page, setSearchParams]);

  // Reset page when query or entity type changes
  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, entityType]);

  const handleSelect = (hit: SearchHit) => {
    searchService.recordSelection({
      query: debouncedQuery,
      entityType: hit.entityType,
      entityId: hit.id,
    });
    navigate(hit.url);
  };

  const totalPages = searchResults
    ? Math.ceil(searchResults.totalCount / PAGE_SIZE)
    : 0;

  // Build dynamic placeholder from metadata shortcuts
  const placeholder = useMemo(() => {
    if (!metadata?.shortcuts || Object.keys(metadata.shortcuts).length === 0) {
      return 'Search...';
    }
    const examples = Object.entries(metadata.shortcuts)
      .slice(0, 3)
      .map(([prefix, entity]) => `${prefix}: ${entity.toLowerCase()}`)
      .join(', ');
    return `Search... (${examples})`;
  }, [metadata]);

  return (
    <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        Search
      </Typography>

      {/* Search controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            autoFocus
            placeholder={placeholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{ flex: 1, minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Entity Type</InputLabel>
            <Select
              value={entityType}
              label="Entity Type"
              onChange={(e) => setEntityType(e.target.value)}
            >
              <MenuItem value="">All types</MenuItem>
              {metadata?.entities.map((entity) => (
                <MenuItem key={entity.name} value={entity.name}>
                  {entity.displayName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Results */}
      {isLoading || isFetching ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : !isQueryEnabled ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            Type to search
          </Typography>
          {metadata?.shortcuts && Object.keys(metadata.shortcuts).length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Use shortcuts like{' '}
              {Object.entries(metadata.shortcuts)
                .slice(0, 3)
                .map(([prefix, entity]) => `"${prefix}:" for ${entity.toLowerCase()}s`)
                .join(', ')}
            </Typography>
          )}
        </Paper>
      ) : searchResults && searchResults.hits.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {shortcutEntityName && cleanQuery.length === 0
              ? `No ${shortcutEntityName.toLowerCase()}s found`
              : `No results found for "${debouncedQuery}"`}
          </Typography>
        </Paper>
      ) : searchResults ? (
        <>
          {/* Results header with entity type filter chips */}
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1.5,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {shortcutEntityName && cleanQuery.length === 0
                  ? `Browsing ${shortcutEntityName.toLowerCase()}s`
                  : `Search completed`}{' '}
                in {searchResults.executionTimeMs}ms
              </Typography>
            </Box>

            {/* Entity type filter chips - clickable to filter */}
            {Object.keys(searchResults.entityCounts).length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label="All"
                  size="small"
                  color={!entityType ? 'primary' : 'default'}
                  variant={!entityType ? 'filled' : 'outlined'}
                  onClick={() => setEntityType('')}
                  sx={{ cursor: 'pointer' }}
                />
                {Object.keys(searchResults.entityCounts).map((type) => (
                  <Chip
                    key={type}
                    label={type}
                    size="small"
                    color={entityType === type ? 'primary' : 'default'}
                    variant={entityType === type ? 'filled' : 'outlined'}
                    onClick={() => setEntityType(entityType === type ? '' : type)}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            )}
          </Box>

          {/* Results list */}
          <Paper>
            <List disablePadding>
              {searchResults.hits.map((hit, index) => (
                <Box key={hit.id}>
                  {index > 0 && <Divider />}
                  <ListItemButton onClick={() => handleSelect(hit)}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography fontWeight={500}>{hit.primary}</Typography>
                          <Chip
                            label={hit.entityType}
                            size="small"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                          {hit.tertiary && (
                            <Chip
                              label={hit.tertiary}
                              size="small"
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          {hit.secondary && (
                            <Typography variant="body2" color="text.secondary">
                              {hit.secondary}
                            </Typography>
                          )}
                          {hit.meta && (
                            <Typography variant="body2" color="text.disabled">
                              {hit.meta}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItemButton>
                </Box>
              ))}
            </List>
          </Paper>

          {/* Pagination and total count */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 3, gap: 1 }}>
            {totalPages > 1 && (
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, newPage) => setPage(newPage)}
                color="primary"
              />
            )}
            <Typography variant="body2" color="text.secondary">
              {searchResults.totalCount}{searchResults.hasMoreResults ? '+' : ''} total results
            </Typography>
          </Box>
        </>
      ) : null}
    </Box>
  );
}
